import { useEffect, useState } from 'react';

const SCREENISTA_MARK_SRC = '/favicon.svg';

/**
 * Compact device silhouette matching live-preview orientation language.
 * Official mark fills the bezel when media is missing or fails to load.
 */
export function DisplayOrientationThumb({
  orientation,
  active = false,
  thumbnailUrl,
  index,
}: {
  orientation?: string;
  active?: boolean;
  thumbnailUrl?: string;
  index?: number;
}) {
  const o = String(orientation || '').toUpperCase();
  const isPortrait = o === 'PORTRAIT';
  const isLandscape = o === 'LANDSCAPE';
  const [imgFailed, setImgFailed] = useState(false);

  useEffect(() => {
    setImgFailed(false);
  }, [thumbnailUrl]);

  const showMedia = Boolean(thumbnailUrl) && !imgFailed;

  return (
    <span
      className={`relative flex h-11 w-9 shrink-0 items-center justify-center overflow-hidden rounded-md ${
        active ? 'bg-[#0B1220]' : 'bg-[#1A2233]'
      }`}
      aria-hidden="true"
      title={isPortrait ? 'Portrait' : isLandscape ? 'Landscape' : 'Screen'}
    >
      {showMedia ? (
        <img
          src={thumbnailUrl}
          alt=""
          className="absolute inset-0 h-full w-full object-cover opacity-80"
          onError={() => setImgFailed(true)}
        />
      ) : null}

      <span
        className={`relative z-[1] flex items-center justify-center overflow-hidden rounded-[3px] border border-white/25 shadow-sm ${
          isPortrait
            ? 'h-[78%] w-[52%]'
            : isLandscape
              ? 'h-[42%] w-[78%]'
              : 'h-[58%] w-[58%]'
        } ${showMedia ? 'bg-black/40' : 'bg-[#0A0F1A]'}`}
      >
        {showMedia ? (
          <span className="absolute inset-[1.5px] rounded-[2px] bg-gradient-to-br from-[#2a3348]/40 to-[#12161f]/40" />
        ) : (
          <img
            src={SCREENISTA_MARK_SRC}
            alt=""
            className="h-[72%] w-auto max-w-[78%] object-contain"
          />
        )}
      </span>

      {typeof index === 'number' ? (
        <span
          className={`absolute top-0.5 right-0.5 z-[2] flex h-3.5 min-w-3.5 items-center justify-center rounded-[3px] px-0.5 text-[8px] font-bold ${
            active
              ? 'bg-[var(--dash-brand)] text-white'
              : 'bg-white/90 text-[#1F2433]'
          }`}
        >
          {index}
        </span>
      ) : null}
    </span>
  );
}

/** Strip redundant orientation suffixes already shown by the device thumb. */
export function cleanScreenDisplayName(name: string) {
  return name
    .replace(/\s*\(\s*(portrait|landscape|square)\s*\)\s*/gi, ' ')
    .replace(/\s*[-–—]\s*(portrait|landscape|square)\s*$/gi, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
}
